/**
 * <StudentActivityCongratsPage> — la fin d'une activité rendue : ce qui s'est passé, ce qu'elle a
 * rapporté, puis la suite au geste de l'élève (page).
 *
 * L'ancien `ActivityFinalizedCongrats`, repris (Wajih, 18/10 : la fin allait trop vite, sans qu'on
 * comprenne ce qui se passait, et sans les animations d'avant) :
 *  - les confettis de l'ancien écran (`<Confetti>`), une fois, par-dessus ;
 *  - le champion qui danse, une de ses animations de félicitations comme avant, et son halo (le
 *    champion du jour, ou Sam après une activité avec lui) ; content et fixe en mouvement réduit ;
 *    son mot et sa phrase (le titre et le sous-titre de sa banque) ;
 *  - ce que l'activité a rapporté, qui arrive tuile après tuile et défile comme avant : les XP, puis
 *    les pétales (seulement quand il y en a), puis le chrono (`congratsReveal`), seulement quand il
 *    a compté du temps : dans un cours partagé, il s'arrête en Dialogue, et une activité faite tout
 *    entière en Dialogue n'a pas de chrono à montrer (Wajih, 21/10) ;
 *  - puis le bouton de la suite (« Activité suivante », « … avec Sam », « Retour à mes activités »
 *    quand il n'en reste pas, ou « Revenir à l'activité » quand l'élève reste dessus : la copie
 *    soumise en Dialogue, le masteur est là), au geste de l'élève. Comme l'ancien, il se presse seul au
 *    bout de 12 s, à compter de son apparition, et se remplit pendant ce temps (`<CountdownButton>`) ; dans
 *    le live, il attend tant que la liste des activités est ouverte par-dessus (`countdownPaused`), et
 *    repart de zéro quand elle se referme ;
 *  - une sortie discrète, pour s'arrêter là : « Retour à mes activités ». Absente quand la suite est
 *    déjà la liste, et quand l'élève revient sur l'activité : il reste dans le live, dont l'en-tête
 *    ouvre la liste (Wajih, 26/10 : « ici ça ne sert à rien de garder le bouton du bas »).
 * Le bouton de la suite et la sortie sont au pied de la page (`footer`), hors de ce qui défile : dans
 * le volet du live, plus court que l'écran, la célébration défile et le bouton reste à portée du
 * doigt. Dans le défilement, il passait sous le bord du volet sur un petit téléphone, et l'élève ne
 * pouvait plus passer l'écran ; l'ancien, plein écran, l'avait toujours sous la main.
 * La célébration tient au-dessus du pied, sans défiler : quand la place manque (un petit téléphone, le
 * volet du live, la bulle tandem gardée au pied), c'est le champion qui rapetisse (`congratsFit`), et
 * les tuiles restent entières au-dessus du pied. Sous son plancher seulement, elle défile.
 * Le jingle de fin, lui, est un effet de l'écran. En live, la bulle tandem reste au coin : c'est elle
 * qui dit le mode (Wajih, 20/10). Posée une fois au-dessus des étapes (`<FloatingSlot>`), le gabarit lui
 * garde sa place.
 *
 * Le déroulé est un état d'interface : il vit ici. `onContinue` part une fois, au geste ou au bout du
 * compte à rebours (le bouton le garde). En mouvement réduit, tout est là d'emblée, les nombres
 * écrits, sans confettis ni remplissage ; le compte à rebours reste.
 */
import React, { useEffect, useRef, useState } from "react"
import { ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChampionFigure } from "../../atoms/ChampionFigure"
import { Confetti } from "../../atoms/Confetti"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { ActivityGainTile } from "../../molecules/ActivityGainTile"
import { CountdownButton } from "../../molecules/CountdownButton"
import { formatCount } from "../../utils/gameAmount"
import { PageTemplate } from "../../templates/PageTemplate"
import { spacing } from "../../theme/tokens/spacing"
import { congratsFigureHeight } from "./congratsFit"
import { AUTO_CONTINUE_MS, congratsRevealFrame } from "./congratsReveal"

/** Ce qui suit la célébration : l'activité suivante, la suivante avec Sam, la liste, ou la même activité. */
export type ActivityCongratsNext = "next" | "sam" | "activities" | "activity"

export type StudentActivityCongratsPageProps = {
    /** Celui qui célèbre : Sam après une activité avec lui, sinon le champion du jour. */
    champion: ChampionName
    /** Son mot, tiré de sa banque. */
    title: string
    /** Sa phrase, sous le mot. Absente : rien. */
    subtitle?: string
    /** L'XP gagnée. */
    xp: number
    /** Les pétales gagnés ; 0 ou absent : la tuile ne se monte pas. */
    petals?: number
    /** Le temps compté, en secondes ; 0 : la tuile ne se monte pas. */
    durationSeconds: number
    /** Défaut `next`. */
    next?: ActivityCongratsNext
    /** Enchaîner vers `next` : au bouton, ou au bout de son compte à rebours. Une fois. */
    onContinue: () => void
    /** « Retour à mes activités » : s'arrêter là. Absent, `next="activities"` ou `"activity"` : pas de sortie. */
    onBackToActivities?: () => void
    /**
     * Le compte à rebours attend : dans le live, la liste des activités est ouverte par-dessus la page. Il repart
     * de zéro quand elle se referme (Wajih, 27/10). Défaut `false`.
     */
    countdownPaused?: boolean
    testID?: string
}

/** L'écran se lit d'un coup d'œil : une colonne de téléphone, même sur un grand écran. */
const CONGRATS_WIDTH = 480
/** Le pas du déroulé : assez fin pour qu'un nombre défile sans à-coup. */
const FRAME_MS = 50

const TEXTS = {
    next: {
        next: "Activité suivante",
        sam: "Activité suivante avec Sam",
        activities: "Retour à mes activités",
        activity: "Revenir à l'activité",
    } satisfies Record<ActivityCongratsNext, string>,
    stop: "Retour à mes activités",
    xp: "XP",
    petals: "Pétales",
    chrono: "Chrono",
}

/** Le temps écoulé depuis l'ouverture, qui avance tant que le déroulé n'est pas fini. */
function useElapsed(running: (elapsed: number) => boolean): number {
    const startedAt = useRef(Date.now())
    const [elapsed, setElapsed] = useState(0)
    const runningRef = useRef(running)
    runningRef.current = running
    useEffect(() => {
        const timer = setInterval(() => {
            const now = Date.now() - startedAt.current
            setElapsed(now)
            if (!runningRef.current(now)) clearInterval(timer)
        }, FRAME_MS)
        return () => clearInterval(timer)
    }, [])
    return elapsed
}

export function StudentActivityCongratsPage(props: StudentActivityCongratsPageProps) {
    const {
        champion,
        title,
        subtitle,
        xp,
        petals = 0,
        durationSeconds,
        next = "next",
        onContinue,
        onBackToActivities,
        countdownPaused = false,
        testID,
    } = props

    const reduceMotion = useReduceMotion()
    const gains = { xp, petals }
    const elapsed = useElapsed((now) => !congratsRevealFrame(gains, now, reduceMotion).settled)
    const frame = congratsRevealFrame(gains, elapsed, reduceMotion)

    const destination = TEXTS.next[next]
    // La place au-dessus du pied, et ce qui suit le champion : il prend ce qui reste.
    const [available, setAvailable] = useState(0)
    const [rest, setRest] = useState(0)
    const figureHeight = congratsFigureHeight(available, rest)

    return (
        <View style={styles.screen} testID={testID}>
            <PageTemplate
                maxContentWidth={CONGRATS_WIDTH}
                padding="4"
                scroll={false}
                contentContainerStyle={styles.flush}
                footer={
                    <View style={styles.next(frame.buttonOpacity)}>
                        <Stack gap="3" align="center">
                            <View style={styles.stretch}>
                                <CountdownButton
                                    label={destination}
                                    onPress={onContinue}
                                    durationMs={AUTO_CONTINUE_MS}
                                    running={frame.buttonVisible && !countdownPaused}
                                    disabled={!frame.buttonVisible}
                                />
                            </View>
                            {next !== "activities" && next !== "activity" && onBackToActivities != null && (
                                <TextButton label={TEXTS.stop} onPress={onBackToActivities} />
                            )}
                        </Stack>
                    </View>
                }
            >
                <ScrollView
                    style={styles.fill}
                    contentContainerStyle={styles.scroll}
                    showsVerticalScrollIndicator={false}
                    onLayout={(event) => setAvailable(event.nativeEvent.layout.height - 2 * MOMENT_PADDING)}
                >
                    <View style={styles.moment}>
                        <ChampionFigure name={champion} pose="dancing" height={figureHeight} halo />
                        <View
                            style={styles.rest}
                            onLayout={(event) => setRest(event.nativeEvent.layout.height + MOMENT_GAP)}
                        >
                            <Stack gap="2" align="center">
                                <Text
                                    size="text-3xl"
                                    weight="bold"
                                    color="text-strong"
                                    center
                                    accessibilityRole="header"
                                >
                                    {title}
                                </Text>
                                {subtitle != null && subtitle !== "" && (
                                    <Text size="text-lg" weight="semibold" color="text-muted" center>
                                        {subtitle}
                                    </Text>
                                )}
                            </Stack>
                            <View style={styles.gains}>
                                {xp > 0 && (
                                    <View style={styles.gain(frame.xpVisible)}>
                                        <ActivityGainTile
                                            kind="xp"
                                            value={`+${formatCount(frame.xp)}`}
                                            label={TEXTS.xp}
                                        />
                                    </View>
                                )}
                                {petals > 0 && (
                                    <View style={styles.gain(frame.petalsVisible)}>
                                        <ActivityGainTile
                                            kind="petals"
                                            value={`+${formatCount(frame.petals)}`}
                                            label={TEXTS.petals}
                                        />
                                    </View>
                                )}
                                {durationSeconds > 0 && (
                                    <View style={styles.gain(frame.chronoVisible)}>
                                        <ActivityGainTile
                                            kind="chrono"
                                            value={formatCountdown(durationSeconds)}
                                            label={TEXTS.chrono}
                                        />
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                </ScrollView>
            </PageTemplate>
            <Confetti variant="activity" />
        </View>
    )
}

/** L'écart entre le champion et son mot, et la marge haute et basse de la célébration : mesurés, ils comptent. */
const MOMENT_GAP = spacing["5"]
const MOMENT_PADDING = spacing["4"]

const styles = StyleSheet.create((theme) => ({
    screen: { flex: 1 },
    /** Le gabarit pose une marge haute et basse : la célébration pose la sienne, mesurée. */
    flush: { paddingVertical: 0 },
    fill: { flex: 1 },
    /** Au milieu de la place quand tout tient ; défile depuis le haut sinon. */
    scroll: { flexGrow: 1, justifyContent: "center", paddingVertical: MOMENT_PADDING },
    moment: { alignItems: "center", gap: MOMENT_GAP },
    rest: { alignSelf: "stretch", gap: theme.spacing["5"] },
    gains: {
        flexDirection: "row",
        alignSelf: "stretch",
        gap: theme.spacing["3"],
    },
    /** Une tuile pas encore là garde sa place : la ligne ne saute pas quand elle arrive. */
    gain: (visible: boolean) => ({ flex: 1, minWidth: 0, opacity: visible ? 1 : 0 }),
    next: (opacity: number) => ({ opacity }),
    stretch: { alignSelf: "stretch" },
}))
